import {AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationStart, Router} from "@angular/router";
import {DOCUMENT, Location} from '@angular/common';
import {Subscription, timer} from "rxjs";
import {delay, filter, finalize, takeUntil} from "rxjs/operators";
import scrollIntoView from 'scroll-into-view-if-needed';

import {JASO} from "../../shared/defines/gukeo.const";
import {ListLoader} from "../../shared/interfaces/list-loader";
import {Homonym} from "../../shared/models/homonym.model";
import {
  DETAIL_POPUP_DELAY, HISTORY_KEY_SEARCH_HOMONYM,
  SearchParam,
  UI_RELOAD_HEIGHT
} from "../../shared/defines/client.const";
import {generateUrlWithQueryParams, GetSearchFromQuery, listItemId} from "../../shared/defines/client.func";
import {
  MENU_PAGE,
  PRELOAD_RATIO,
  HOMONYM_SCROLL_SPEED,
  LIST_SCROLL_INTERVAL, EXT_HEIGHT_CAL, BODY_HEIGHT_VALUE, BODY_HEIGHT_CAL, SIDE_HEIGHT_DELTA
} from 'src/app/shared/defines/desktop.const';
import {VoiceService} from "../../shared/services/voice/voice.service";
import {HomonymService} from "../../shared/services/homonym/homonym.service";
import {WordHistoryService} from "../../shared/services/word/word-history.service";
import {GlobalEventService} from "../../shared/services/global-event/global-event.service";
import {SearchBoxComponent} from "../../shared/components/search-box/search-box.component";
import {GetHomonymName} from "../../shared/defines/gukeo.func";
import {LastIdService} from "../../shared/services/last-id/last-id.service";
import {SimpleWord} from "../../shared/models/simple-word.model";


@Component({
  selector: 'app-homonym',
  templateUrl: './homonym.component.html',
  styleUrls: ['./homonym.component.scss']
})
export class HomonymComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('container') container?: ElementRef;
  @ViewChild('reload') reload?: ElementRef;
  @ViewChild('innerScroll') innerScroll?: ElementRef;
  @ViewChild('main', { static: true }) main?: ElementRef; // ngOnInit() 에서 사용 가능

  // 화면 사이즈 맞춤에 사용
  @ViewChild('extContainer') extContainer?: ElementRef;
  @ViewChild(SearchBoxComponent) extHeader?: SearchBoxComponent;
  @ViewChild('extBody') extBody?: ElementRef;
  @ViewChild('extBody2') extBody2?: ElementRef;
  @ViewChild('listHomonym') listHomonym?: ElementRef;

  protected readonly MENU_PAGE = MENU_PAGE;
  protected readonly HISTORY_KEY_SEARCH_HOMONYM = HISTORY_KEY_SEARCH_HOMONYM;
  protected readonly listItemId = listItemId;

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;

  // 자소 인덱스
  protected curJasoIdx: JASO | null = null;

  // 검색창에 넣는 값
  public keyword: string | null = null;
  public placeholder: string | null = '검색어를 입력 해 보시오.';
  protected scrollTop = true;   // 스크롤이 맨 위에 있는지 여부, 검색창의 호랑이를 헤더 아래로 감출 때 사용한다.

  // ListLoader 설정
  protected ll: ListLoader<Homonym>;
  private getNextList$: Subscription | null = null;
  private needReload = false;
  protected get lastId(): string | null {
    const ll = this.ll?.getList() ?? null;
    const lastHomonym = ll ? ll[ll.length - 1] : null;
    if (lastHomonym?.listWord.length === 0) {
      throw new Error('HomonymComponent::lastId() - lastHomonym.listWord.length is 0');
    }
    return lastHomonym ? lastHomonym.listWord[lastHomonym.listWord.length - 1].id : null;
  }

  protected curWord: string | null = null; // 현재 선택된 이운이의어의 단어 ID

  protected autoPlay = false; // 화면에 처음 들어왔을 때, 자동 재생을 막는 변수.
  private lazyAutoPlay = false;

  private bodyHeight = UI_RELOAD_HEIGHT;

  private inited = false;

  // 라우딩 처리
  private router$: Subscription | null = null;

  private queryParams$?: Subscription;
  private params$?: Subscription;

  // 스크롤 처리
  private scrollDownInterval: any = null;
  protected showScrollDown = false;

  protected isNoResult(): boolean {
    return (this.ll?.IsComplete() ?? false) && (this.ll?.getList()?.length == 0 ?? false);
  }


  constructor(
    @Inject(DOCUMENT) private document: Document,
    protected route: ActivatedRoute,
    protected router: Router,
    protected homonymSvc: HomonymService,
    protected historySvc: WordHistoryService,
    private location: Location,
    private voice: VoiceService,
    private lastIdSvc: LastIdService,
    private globalEventSvc: GlobalEventService,
  ) {
    // super(globalEventSvc);

    /******************************************************************************************
     * 주소 뒤의 파라미터를 받아오는 곳.
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        console.log('HomonymComponent::constructor() queryParams =', params);
        if ('serial' in params) {
          delete params['serial'];
        }
        this.queryParams = {...params};

        if (this.inited) {  // 이미 로딩된 경우 == 화면이 생성된 상태

          if ('id' in this.queryParams) {
            const id = this.queryParams.id;
            // console.log('queryParams:: id=', id);

            const processParams = () => {
              if (this.queryParams?.id) {
                const homonymName = GetHomonymName(this.queryParams.id);
                if (this.ll.getById(homonymName)) {
                  this.CloseDetail();
                  this.OpenDetail(this.queryParams.id);

                  this.scrollToHomonym(homonymName);
                }
                else {
                  this.init();
                }
              }
              else {
                throw new Error('HomonymComponent::constructor() - queryParams.id is null');
              }
            };

            if (this.homonymSvc.IsLoading()) {
              this.homonymSvc.GetNextList().pipe(
                delay(DETAIL_POPUP_DELAY),
              ).subscribe(processParams);
            } else {
              processParams();
            }
          }
          else {
            this.init();
          }
        }
      });

    // lm.Reset();
    this.ll = homonymSvc;

    /******************************************************************************************
     * 라우터 파라미터를 읽어오는 곳
     * 처음 이 페이지에 들어올 때 불려진다.
     * url이 변경되어도 불린다.
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    this.params$ = route.params.subscribe(params => {
      console.log('mComponent::constructor() params =', params, this.queryParams);

      this.inited = true;
      this.init();
    });
  }

  private init(): void {
    let param: SearchParam;
    // [param, this.curJasoIdx] = GetSearchFromQuery(this.queryParams, 'ㄱ');
    [param, this.curJasoIdx, this.keyword] = GetSearchFromQuery(this.queryParams);
    if (this.queryParams?.id && !this.queryParams?.start) {
      const homonymName = GetHomonymName(this.queryParams.id);
      param.start = homonymName;
    }
    this.ReloadList(param);

  }

  ngOnInit(): void {
    this.router$ = this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
    ).subscribe((event) => {
      this.onLeave();
    });

    // if (this.main) {
    //   this.InitFocusMain(this.main);
    // }
    // else {
    //   console.error('HomonymComponent::ngOnInit() main is null');
    // }
  }

  ngAfterViewInit() {
    this.updateExtHeight();

    // 최초 로딩 시, 화면 배치가 어그러지는 것을 고치기위한 코드. (TODO: 효과 있는지 확인 후 필요시 제거)
    timer(2000).subscribe(() => {
      this.updateExtHeight();
    });
  }

  ngOnDestroy(): void {
    if (this.getNextList$) {
      this.getNextList$.unsubscribe();
      this.getNextList$ = null;
    }

    this.CloseDetail();

    // router 이동 체크를 중지함.
    this.router$?.unsubscribe();

    // this.ExitFocusMain();
    this.queryParams$?.unsubscribe();
    this.params$?.unsubscribe();

    // 추가: 스크롤 다운 타이머 종료 처리
    if (this.scrollDownInterval !== null) {
      clearInterval(this.scrollDownInterval);
      this.scrollDownInterval = null;
    }

    // 소리 재생 중지.
    // TODO: routerStop$ 처리 시 하고 있는데 또 반복적으로 해야 할까?
    this.voice.Stop();
  }

  private onLeave(): void {
    this.voice.Stop();

    if (this.listHomonym) {
      const children: HTMLElement[] = Array.from(this.listHomonym.nativeElement.children);
      const listItem = children.find((el: HTMLElement) => {
        return el.id && this.listHomonym && el.offsetTop > this.container?.nativeElement.scrollTop;
      });
      if (listItem?.id) {
        this.lastIdSvc.setLastHomonymId(this.curWord, listItem.id);
        // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
        // Keep `id`; `start` restores list position, `id` restores selected detail.
        const queryParam: SearchParam = {
          ...(this.queryParams ? this.queryParams : {}),
          start: listItem.id,
        }
        const basePath = this.router.url.split('?')[0];  // query parameter를 제거
        const urlWithParams = generateUrlWithQueryParams(basePath, queryParam);
        // console.warn('HomonymComponent::onLeave() urlWithParams=', urlWithParams);

        // URL 변경
        this.location.replaceState(urlWithParams);
      }
    }
    else {
      console.error('HomonymComponent::onLeave() listItem is null');
    }
  }


  onChildViewInit(word: SimpleWord) {
    if (word.id === this.lastId) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  /****************************************************************************
   * 브라우저 크기 변경 처리.
   ****************************************************************************/


  // 'resize' 이벤트를 감지하고 이벤트 발생 시 updateHeight 메소드를 호출합니다.
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.updateExtHeight();
  }

  private updateExtHeight(): void {
    if (this.extContainer && this.extHeader) {
      const containerHeight = this.extContainer.nativeElement.offsetHeight;
      const headerHeight = this.extHeader.offsetHeight;
      this.bodyHeight = containerHeight - headerHeight - EXT_HEIGHT_CAL;

      if (this.extBody) {
        this.extBody.nativeElement.style.height = this.bodyHeight + 'px';
      }

      if (this.extBody2) {
        const bodyHeight2 = Math.max(
          BODY_HEIGHT_VALUE - SIDE_HEIGHT_DELTA,
          this.bodyHeight + BODY_HEIGHT_CAL);
        this.extBody2.nativeElement.style.setProperty('--side-height', bodyHeight2 + 'px');
      }
    }
  }


  /****************************************************************************
   *
   ****************************************************************************/

  onSelectWord(word_id: string) {
    const queryParams: SearchParam = {
      ...(this.queryParams ?? {}),
      id: word_id,
    };
    // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
    this.router.navigate(['homonym'], {queryParams}).then();
  }

  /**
   * onSelectWord()로 처리하는 영역이 너무 좁아서, 추가로 이벤트 처리를 보강.
   */
  onClickWord($event: MouseEvent, word_id: string): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.onSelectWord(word_id);
  }


  protected onSearch(param: SearchParam) {
    console.log('HomonymComponent::onSearch()', param);

    this.router.navigate(['/homonym'], {
      queryParams: {...param}
    }).then();
  }

  private ReloadList(param: SearchParam) {
    console.log('HomonymComponent::ReloadList() param=', param);

    this.showScrollDown = false;
    
    this.ll.ResetList(param);
    this.loadList(true);

    this.autoPlay = false;
    this.lazyAutoPlay = false;
  }

  private loadList(cancel = false): void {
    if (this.getNextList$) {
      console.log('WordComponent::loadWordList() already call loadWordList!');
      if (cancel) {
        this.getNextList$.unsubscribe();
      }
      else {
        return;
      }
    }

    if (this.ll.IsComplete()) {
      console.warn('WordComponent::loadWordList() already load all word!');
      return;
    }

    this.getNextList$ = this.ll.GetNextList().pipe(
      finalize(() => {
        this.getNextList$ = null;

        if (this.needReload) {
          timer(0).subscribe(() => {
            this.needReload = false;
            this.loadList();
          });
        }
      })
    ).subscribe(
      data => {
        // console.log(data);

        if (this.queryParams?.id) {
          // 바로 보기 상태라면, 서버로부터 로딩이 완료되고 나서 바로 상세 화면을 연다.
          // TODO: init() 쪽으로 코드를 옮기기.
          //  앱에서와 다르게 (목록과 상세 화면이 분리되어 있는 경우) 여기까지 와서 OpenDetail을 할 필요가 없어 보인다.
          this.OpenDetail(this.queryParams.id, true);
        }
        else
        {
          // 로딩 된 항목이 한개 이상이라면 첫번째 항목을 보여준다.
          const firstHomonym = this.ll?.getList()?.at(0);
          const firstWord = firstHomonym?.listWord?.at(0);
          this.OpenDetail(firstWord?.id ?? null, false);
        }
      },
      err => console.error(err)
    );
  }


  protected OpenDetail(word_id: string | null, addHistory = true) {
    this.curWord = word_id;

    if (addHistory && word_id) {
      this.historySvc.addHistory(word_id);
    }

    /**
     * 화면에 처음 들어올 때는 자동 재생을 막고, 그 다음부터 자동 재생 되도록 한다.
     * 화면에 처음 들어올 때, 무조건 첫번째 항목을 상세 화면에 표시하도록 되어있다. 이때는 소리 안 나야 함.
     */
    if (!this.lazyAutoPlay) {
      this.lazyAutoPlay = true;
    }
    else {
      this.autoPlay = true;
    }
  }

  protected CloseDetail() {
    this.curWord = null;
  }

  /****************************************************************************
   * 일정 범위 이상 스크롤되면 남은 단어를 자동 로딩한다.
   ****************************************************************************/
  protected onScroll($event: Event): void {
    // console.log('WordComponent::onScroll()', $event);

    if (!this.container) {
      throw new Error('WordComponent::onScroll() - #container does not exist!!');
    }

    if (this.ll && !this.ll.IsLoading() && !this.ll.IsComplete() && this.reload) {
      const tar = $event.target || $event.srcElement || $event.currentTarget;
      const elContainer = this.container.nativeElement;
      if (tar !== elContainer) {
        console.error('WordComponent::onScroll() - wrong structure!!');
      }

      const elReload = this.reload.nativeElement;
      // console.warn('onScroll() ', elReload.offsetTop ?? null, elContainer?.scrollTop ?? null, UI_RELOAD_HEIGHT, elContainer ?? null);
      // NOTE: 앱은 스크롤되는 윗 부분에 빈 공간이 있지만, 책상형은 공간이 없다. 그래서 innerHeight 반영하지 않는다.
      if (elReload.offsetTop < elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight) {
        if (this.getNextList$ === null) {
          this.loadList();
        }
        else {
          // 스크롤 중에 이미 로딩이 진행되고 있으면, 로딩이 끝난 뒤에 다시 로딩이 되도록 요청. (비동기처리 순서 꼬임 방지)
          this.needReload = true;
        }
      }

    }
  }

  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // this.container?.nativeElement.scrollTo({
    //   top: 0,
    //   behavior: 'smooth',
    //   // behavior: 'auto',
    // });

    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }

  protected onClickRope($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    if (this.container) {
      this.container.nativeElement.scrollTop = 0;
    }
  }

  /**
   * 이운이의어를 선택하면, 왼쪽 목록에서 해당 이운이의어가 보이도록 스크롤 시킨다.
   * @param word_id
   * @private
   */
  private scrollToHomonym(homonymName: string): void {
    const el: HTMLElement | null = this.document.getElementById(listItemId(homonymName));

    if (el) {
      scrollIntoView(el, {
        behavior: 'smooth',
        block: 'start',
        scrollMode: 'if-needed',
      });
    }
    else {
      console.error('HomonymComponent::scrollToHomonym() - not found element', homonymName);
    }
  }

  onStartScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onStartScrollDown()', $event);
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // 한번은 바로 내려간다.
    this.container?.nativeElement.scrollTo({
      top: this.container.nativeElement.scrollTop + HOMONYM_SCROLL_SPEED * 2,
      behavior: 'auto',
    });

    this.scrollDownInterval = setInterval(() => {
      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + HOMONYM_SCROLL_SPEED,
        behavior: 'auto',
      });
    }, LIST_SCROLL_INTERVAL);
  }

  onEndScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onEndScrollDown()', $event);
    clearInterval(this.scrollDownInterval);
    this.scrollDownInterval = null;
  }

  /****************************************************************************
   * 기타
   ***************************************************************************/

  protected max(a: number, b: number): number {
    return Math.max(a, b);
  }
}
